/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { HStack, IconButton, Progress, Spinner, Text } from '@chakra-ui/react';
import { useEffect, useRef, useState, type JSX } from 'react';
import { BsFillPlayFill } from 'react-icons/bs';
import { GrPauseFill } from 'react-icons/gr';
import { ColorMap } from '@/themes/Attributes';

export function convertMsToTime(milliseconds: number) {
  let seconds = Math.floor(milliseconds / 1000);
  let minutes = Math.floor(seconds / 60);

  seconds = seconds % 60;
  minutes = minutes % 60;

  return `${padTo2Digits(minutes)}:${padTo2Digits(seconds)}`;
}

function padTo2Digits(num: number) {
  return num.toString().padStart(2, '0');
}

type AudioRendererProps = {
  src?: string;
  start?: number;
  end?: number;
};
export function AudioRenderer(props: AudioRendererProps) {
  const audioElement = useRef<HTMLAudioElement | null>(null);

  // Flags
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Progress
  const [progress, setProgress] = useState(0);

  // Duration related state / variables
  const [durationMs, setDurationMs] = useState(0);
  const segmentStart = props.start ?? 0;
  const segmentEnd = props.end ?? durationMs;
  const segmentDuration = segmentEnd - segmentStart;

  // Generate timestamps
  const durationTimestamp = convertMsToTime(segmentDuration);
  const currTimestamp = convertMsToTime((segmentDuration * progress) / 100);

  useEffect(() => {
    if (audioElement.current == null) {
      // Create audio element and set properties
      audioElement.current = document.createElement('audio');
      audioElement.current.hidden = true;

      // Register Events
      audioElement.current.addEventListener('loadstart', () => setIsLoading(true));
      audioElement.current.addEventListener('canplay', () => setIsLoading(false));
      audioElement.current.addEventListener('play', () => setIsPlaying(true));
      audioElement.current.addEventListener('pause', () => setIsPlaying(false));
      audioElement.current.addEventListener('ended', () => {
        setIsPlaying(false);
        setProgress(0);
      });

      audioElement.current.addEventListener('loadedmetadata', () => {
        if (audioElement.current != null) {
          setDurationMs(Math.ceil(audioElement.current.duration * 1000));
        }
      });
    }

    // Load audio source
    audioElement.current.src = props.src ?? '/sample_audio.mp3';
    audioElement.current.load();

    // Seek to start
    audioElement.current.currentTime = segmentStart / 1000;
  }, [props.src, segmentStart]);

  // Handle Progress
  useEffect(() => {
    if (audioElement.current == null) {
      return;
    }

    // Required for smooth progress animation
    const animateProgress = () => {
      if (audioElement.current != null && durationMs != 0) {
        const currentMs = Math.ceil(audioElement.current.currentTime * 1000);

        if (currentMs >= segmentEnd) {
          pause();
          return;
        }

        const progressMs = currentMs - segmentStart;
        const segmentDuration = segmentEnd - segmentStart;

        const progress = (progressMs * 100) / segmentDuration;
        setProgress(progress);

        if (!audioElement.current.paused) {
          requestAnimationFrame(animateProgress);
        }
      }
    };
    let animationHandle: number | null = null;
    audioElement.current.addEventListener('play', () => {
      animationHandle = requestAnimationFrame(animateProgress);
    });
    audioElement.current.addEventListener('pause', () => {
      if (animationHandle != null) {
        cancelAnimationFrame(animationHandle);
      }
    });

    return () => {
      if (animationHandle != null) {
        cancelAnimationFrame(animationHandle);
      }
    };
  }, [durationMs, segmentStart, segmentEnd]);

  const play = () => {
    if (!audioElement.current) {
      return;
    }
    const currentMs = Math.ceil(audioElement.current.currentTime * 1000);
    // Seek to start position
    if (segmentStart > currentMs || currentMs >= segmentEnd) {
      audioElement.current.currentTime = segmentStart / 1000;
    }

    audioElement.current.play().catch((err) => {
      console.error('Error while playing audio:', err);
    });
    setIsPlaying(true);
  };

  const pause = () => {
    if (!audioElement.current) {
      return;
    }

    audioElement.current.pause();
    setIsPlaying(false);
  };

  const handlePlayPause = () => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  };

  let playPauseBtn: JSX.Element;
  if (isLoading) {
    playPauseBtn = <Spinner />;
  } else {
    playPauseBtn = (
      <IconButton aria-label="PlayPause" onClick={handlePlayPause} size="sm" background={ColorMap.white}>
        {isPlaying ? <GrPauseFill /> : <BsFillPlayFill />}
      </IconButton>
    );
  }

  return (
    <HStack width="100%" minWidth="220px" direction="row" alignItems="center" gap="8px" position="relative">
      {playPauseBtn}
      <Progress value={progress} flex={1} borderRadius="full" />
      <Text fontSize="xs" fontWeight="medium">
        {isLoading ? '--:--' : isPlaying ? currTimestamp : durationTimestamp}
      </Text>
    </HStack>
  );
}
